import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TweenLite} from "gsap";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {BookOfShadowsParsedInitData} from "../server/data/BookOfShadowsParsedInitData";

export class BookOfShadowsBackground extends BaseView {
    private _fsFrame: PIXI.Sprite;
    private _fsFrameShow: boolean = false; // shall be resized.

    private _mode: string = "";
    private _nextMode: string = "";

    /**
     * @description To toggle the FS frame on and off.
     * @param on
     */
    public toggleFSFrame(on: boolean): void {
        if (on) {
            this._fsFrame.alpha = 0;
            this._fsFrame.visible = true;
            this._fsFrame.scale.set(this.getFSFrameScaleX(), this.getFSFrameScaleY());
            TweenLite.to(this._fsFrame, 1, {alpha: 1, onComplete: () => (this._fsFrameShow = true)});
        } else {
            TweenLite.to(this._fsFrame, 1, {
                alpha: 0, onComplete: () => {
                    this._fsFrameShow = false;
                    this._fsFrame.visible = false;
                }
            });
        }
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        const layer: ScalableLayer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.BACKGROUND.name),
            bg: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("bg")),
            pos: number = 360;

        layer.addChild(this);

        bg.position.set(pos);
        new SpineTween(bg, 0, "BG_Anim_Loop", -1);

        this.addChild(bg);

        this.createFSFrame();

        this.onGameReady();
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners() {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    /**
     * @inheritDoc
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);

        if (this._fsFrameShow) {
            this._fsFrame.scale.set(this.getFSFrameScaleX(), this.getFSFrameScaleY());
        }
    }

    /**
     * @description To create the FS frame, which acts as soft red borders as device borders.
     */
    private createFSFrame(): void {
        this._fsFrame = new PIXI.Sprite(GameResources.getTextures("FSFrame")[0]);
        this._fsFrame.visible = false;
        this._fsFrame.blendMode = PIXI.BLEND_MODES.ADD;
        this._fsFrame.anchor.set(0.5);
        this._fsFrame.position.set(BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2, BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2); // centre is 360, 360
        StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.FS_FOREGROUND.name).addChild(this._fsFrame);
    }

    /**
     * @description Handler for init data parsed.
     * @param data
     */
    private onInitDataParsed(data: BookOfShadowsParsedInitData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;

        this.onGameReady();

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    /**
     * @description To get the X-scale for FS frame as per device bounds.
     */
    private getFSFrameScaleX(): number {
        return MathHelper.floorToDecimals(NolimitApplication.screenBounds.width / this._fsFrame.texture.width, 2) + 0.03;
    }

    /**
     * @description To get the Y-scale for FS frame as per device bounds.
     */
    private getFSFrameScaleY(): number {
        return MathHelper.floorToDecimals(NolimitApplication.screenBounds.height / this._fsFrame.texture.height, 2) + 0.03;
    }

    /**
     * @description Called from init animations and init data parsed.
     */
    private onGameReady(): void {
        if (this._mode === BookOfShadowsGameMode.FREESPIN && this._nextMode === BookOfShadowsGameMode.FREESPIN && this._fsFrame) {
            this.toggleFSFrame(true);
        }
    }
}